import { useState } from 'react'
import AgeGroupSelector from '../../components/AgeGroupSelector'
import LanguageSelector from '../../components/LanguageSelector'
import { getRebusPuzzles, type StaticRebusPuzzle, type PuzzleTile } from '../../lib/rebus/puzzles'
import { fetchRebusPuzzles } from '../../lib/api'
import { emojiUrl } from '../../lib/rebus/twemoji'
import type { AgeGroup, Language, RebusConfig, RebusCount } from '../../types'
import { LANGUAGE_FLAGS } from '../../types'

// ─── Constants ────────────────────────────────────────────────────────────────

const defaultConfig: RebusConfig = {
  ageGroup: 'young',
  language: 'en',
  count:    4,
}

const COUNTS: RebusCount[] = [1, 4, 8]
const COUNT_LABELS: Record<RebusCount, string> = { 1: '1 puzzle', 4: '4 puzzles', 8: '8 puzzles' }
const MAX_WRONG = 3

// ─── Tile display ─────────────────────────────────────────────────────────────

function TileDisplay({ tile, hintShown }: { tile: PuzzleTile; hintShown: boolean }) {
  if (tile.kind === 'image') {
    return (
      <div className="card p-2 text-center min-w-[68px] flex flex-col items-center gap-1">
        <img
          src={emojiUrl(tile.emoji)}
          alt={tile.word}
          width={48}
          height={48}
          draggable={false}
        />
        {/* Reserve the same height whether hint is shown or not to avoid layout shifts */}
        <span
          className="text-[11px] font-black tracking-wide leading-tight"
          style={{ color: hintShown ? '#888' : 'transparent', userSelect: 'none' }}
        >
          {tile.word}
        </span>
      </div>
    )
  }
  if (tile.kind === 'op') {
    return (
      <span className="text-3xl font-black text-bubble-coral self-center px-0.5">{tile.symbol}</span>
    )
  }
  return (
    <div className="rounded-[14px] border-2 border-[#4ECDC4] bg-[#e8faf9] px-4 py-2.5 text-center min-w-[56px] self-center">
      <span className="text-xl font-black text-teal-700 tracking-wide">{tile.text}</span>
    </div>
  )
}

// ─── Answer boxes ─────────────────────────────────────────────────────────────

function AnswerBoxes({
  answer,
  guess,
  checked,
  correct,
}: {
  answer:  string
  guess:   string
  checked: boolean
  correct: boolean
}) {
  return (
    <div className="flex gap-1.5 justify-center flex-wrap">
      {answer.split('').map((letter, i) => {
        const userLetter = (guess[i] ?? '').toUpperCase()
        const done = checked && correct
        const wrong = checked && !correct && userLetter && userLetter !== letter
        const right = checked && !correct && userLetter === letter
        const bg = done
          ? 'bg-emerald-100 border-emerald-400 text-emerald-800'
          : right
          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
          : wrong
          ? 'bg-red-100 border-red-300 text-red-700'
          : 'bg-white border-gray-300 text-bubble-text'
        return (
          <div
            key={i}
            className={`w-9 h-10 rounded-[8px] border-2 flex items-center justify-center font-black text-base transition-colors ${bg}`}
          >
            {userLetter}
          </div>
        )
      })}
    </div>
  )
}

// ─── Single puzzle view ───────────────────────────────────────────────────────

function PuzzleView({
  puzzle,
  language,
  onNext,
  isLast,
}: {
  puzzle:   StaticRebusPuzzle
  language: Language
  onNext:   () => void
  isLast:   boolean
}) {
  const [input,      setInput]      = useState('')
  const [checked,    setChecked]    = useState(false)
  const [correct,    setCorrect]    = useState(false)
  const [wrongCount, setWrongCount] = useState(0)
  const [hintShown,  setHintShown]  = useState(false)

  const canSkip   = wrongCount >= MAX_WRONG
  const isDone    = correct || canSkip
  const { tiles, answer, hint } = puzzle

  function handleCheck() {
    const norm = input.trim().toUpperCase()
    if (norm === answer) {
      setCorrect(true)
      setChecked(true)
    } else {
      const next = wrongCount + 1
      setWrongCount(next)
      setChecked(true)
      // Clear checked after brief flash
      setTimeout(() => setChecked(false), 900)
    }
  }

  function handleInput(val: string) {
    setInput(val.toUpperCase())
    if (checked && !correct) setChecked(false)
  }

  function handleSpecialChar(ch: string) {
    if (input.length < answer.length) {
      setInput((prev) => prev + ch)
    }
  }

  return (
    <div className="space-y-6">
      {/* ── Tiles ── */}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {tiles.map((tile, i) => (
          <TileDisplay key={i} tile={tile} hintShown={hintShown} />
        ))}
        <span className="text-3xl font-black text-bubble-coral self-center px-0.5">=</span>
        <div className="card p-2 text-center min-w-[68px] flex flex-col items-center justify-center gap-1" style={{ minHeight: 72 }}>
          {correct || canSkip ? (
            <>
              <span className="text-xl font-black text-teal-700">{answer}</span>
              {canSkip && !correct && (
                <span className="text-[10px] text-bubble-muted">answer revealed</span>
              )}
            </>
          ) : (
            <span className="text-bubble-muted font-black" style={{ letterSpacing: '0.15em' }}>
              {'? '.repeat(answer.length).trim()}
            </span>
          )}
        </div>
      </div>

      {/* ── Answer boxes ── */}
      <AnswerBoxes answer={answer} guess={input} checked={checked} correct={correct} />

      {/* ── Status message ── */}
      {correct && (
        <p className="text-center font-black text-teal-700 text-lg">🎉 Correct!</p>
      )}
      {checked && !correct && (
        <p className="text-center font-black text-red-500 text-sm">
          {wrongCount >= MAX_WRONG
            ? 'Nice try! Showing the answer above.'
            : `Not quite — try again! (${MAX_WRONG - wrongCount} attempt${MAX_WRONG - wrongCount !== 1 ? 's' : ''} left)`}
        </p>
      )}

      {/* ── Input + special chars ── */}
      {!isDone && (
        <div className="space-y-2">
          <input
            type="text"
            value={input}
            onChange={(e) => handleInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && input.trim() && handleCheck()}
            placeholder={`Type your answer (${answer.length} letters)`}
            className="w-full rounded-[16px] border-2 border-gray-200 px-4 py-3 text-base font-black text-center text-bubble-text focus:outline-none focus:border-bubble-teal uppercase tracking-widest"
            maxLength={answer.length + 2}
            autoComplete="off"
            spellCheck={false}
          />

          {/* Slovenian special-character picker */}
          {language === 'sl' && (
            <div className="flex gap-2 justify-center">
              {['Č', 'Š', 'Ž'].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => handleSpecialChar(ch)}
                  className="sel-btn text-base font-black px-4 py-2 min-w-[44px]"
                  aria-label={`Add ${ch}`}
                >
                  {ch}
                </button>
              ))}
              {input.length > 0 && (
                <button
                  type="button"
                  onClick={() => setInput((p) => p.slice(0, -1))}
                  className="sel-btn text-base font-black px-3 py-2"
                  aria-label="Delete last character"
                >
                  ⌫
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Hint ── shown on request, or automatically once correct ── */}
      {(hintShown || correct) && (
        <div className="rounded-[14px] bg-[#fff9e6] border-2 border-[#FFE66D] px-4 py-3 text-center">
          <p className="text-sm font-black text-yellow-800">💡 {hint}</p>
        </div>
      )}

      {/* ── Buttons ── */}
      <div className="flex gap-3 justify-center flex-wrap">
        {!isDone && (
          <>
            <button
              onClick={handleCheck}
              disabled={!input.trim()}
              className="btn-primary px-6 py-2.5 text-sm disabled:opacity-40"
            >
              Check ✓
            </button>
            <button
              onClick={() => setHintShown(true)}
              className="btn-secondary px-6 py-2.5 text-sm"
            >
              💡 Hint
            </button>
          </>
        )}
        <button
          onClick={onNext}
          disabled={!isDone}
          className={`px-6 py-2.5 text-sm font-black rounded-full transition-all ${
            isDone
              ? 'btn-primary'
              : 'opacity-30 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isLast ? 'Finish 🏁' : 'Next →'}
        </button>
      </div>
    </div>
  )
}

// ─── Completion screen ────────────────────────────────────────────────────────

function CompletionScreen({
  count,
  onRestart,
  onPrint,
  printing,
}: {
  count:    number
  onRestart: () => void
  onPrint:   () => void
  printing:  boolean
}) {
  return (
    <div className="text-center space-y-6 py-8">
      <div className="text-6xl">🏆</div>
      <div>
        <p className="font-display font-black text-2xl text-bubble-text mb-1">All done!</p>
        <p className="text-bubble-muted">You solved {count} rebus puzzle{count !== 1 ? 's' : ''}.</p>
      </div>
      <div className="flex gap-3 justify-center flex-wrap">
        <button onClick={onRestart} className="btn-secondary px-6 py-2.5">
          ↺ New Puzzles
        </button>
        <button
          onClick={onPrint}
          disabled={printing}
          className="btn-primary px-6 py-2.5 disabled:opacity-60"
        >
          {printing ? '⏳ Generating…' : '🖨 Print Worksheet'}
        </button>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function RebusPage() {
  const [config,   setConfig]   = useState<RebusConfig>(defaultConfig)
  const [puzzles,  setPuzzles]  = useState<StaticRebusPuzzle[]>([])
  const [current,  setCurrent]  = useState(0)
  const [finished, setFinished] = useState(false)
  const [printing, setPrinting] = useState(false)

  const started = puzzles.length > 0

  async function handleGenerate() {
    // Try API first; fall back to static bank if unavailable
    const apiPuzzles = await fetchRebusPuzzles(config.language, config.ageGroup, config.count)
    const ps = apiPuzzles ?? getRebusPuzzles(config.language, config.ageGroup, config.count)
    setPuzzles(ps)
    setCurrent(0)
    setFinished(false)
  }

  function handleNext() {
    if (current < puzzles.length - 1) {
      setCurrent((n) => n + 1)
    } else {
      setFinished(true)
    }
  }

  function handleRestart() {
    setPuzzles([])
  }

  async function handlePrint() {
    if (!puzzles.length) return
    setPrinting(true)
    try {
      const { createElement } = await import('react')
      const { downloadPDF }   = await import('../../lib/pdf/download')
      const { RebusDocument } = await import('../../lib/pdf/rebus')
      const filename = `rebus-${config.language}-${config.ageGroup}-${puzzles.length}x.pdf`
      await downloadPDF(
        createElement(RebusDocument, { puzzles, config }),
        filename,
      )
    } catch (err) {
      console.error('PDF generation failed:', err)
      alert('PDF generation failed — check the browser console for details.')
    } finally {
      setPrinting(false)
    }
  }

  // ── Config screen ──────────────────────────────────────────────────────────

  if (!started) {
    return (
      <div className="max-w-lg">
        <h1 className="font-display font-black text-3xl text-bubble-text mb-1">Rebus</h1>
        <p className="text-bubble-muted mb-8">Decode pictures to find the hidden words.</p>

        <div className="card p-6 space-y-6">
          <AgeGroupSelector
            value={config.ageGroup}
            onChange={(ageGroup: AgeGroup) => setConfig((p) => ({ ...p, ageGroup }))}
          />

          <LanguageSelector
            value={config.language}
            onChange={(language: Language) => setConfig((p) => ({ ...p, language }))}
          />

          <div>
            <label className="config-label">Number of Puzzles</label>
            <div className="flex gap-2" role="radiogroup" aria-label="Number of puzzles">
              {COUNTS.map((c) => (
                <button
                  key={c}
                  role="radio"
                  aria-checked={config.count === c}
                  onClick={() => setConfig((p) => ({ ...p, count: c }))}
                  className={config.count === c ? 'sel-btn-active' : 'sel-btn'}
                >
                  {COUNT_LABELS[c]}
                </button>
              ))}
            </div>
          </div>

          <button onClick={handleGenerate} className="btn-primary w-full text-base py-3">
            Generate Puzzles →
          </button>
        </div>
      </div>
    )
  }

  // ── Game screen ────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="font-display font-black text-2xl text-bubble-text">
            Rebus {LANGUAGE_FLAGS[config.language]}
          </h1>
          {!finished && (
            <p className="text-sm text-bubble-muted">
              Puzzle {current + 1} of {puzzles.length} · {config.ageGroup}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={handleRestart} className="btn-secondary text-sm px-4 py-2">
            ↺ New
          </button>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {printing ? '⏳' : '🖨'} Print
          </button>
        </div>
      </div>

      {/* Progress dots */}
      {!finished && (
        <div className="flex gap-2 mb-6 justify-center">
          {puzzles.map((_, i) => (
            <div
              key={i}
              className={`h-2.5 rounded-full transition-all ${
                i < current
                  ? 'w-2.5 bg-bubble-teal'
                  : i === current
                  ? 'w-6 bg-bubble-coral'
                  : 'w-2.5 bg-gray-200'
              }`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="card p-6">
        {finished ? (
          <CompletionScreen
            count={puzzles.length}
            onRestart={handleRestart}
            onPrint={handlePrint}
            printing={printing}
          />
        ) : (
          <PuzzleView
            key={current}
            puzzle={puzzles[current]}
            language={config.language}
            onNext={handleNext}
            isLast={current === puzzles.length - 1}
          />
        )}
      </div>
    </div>
  )
}
